export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  files?: { name: string; type: string }[];
}

export interface FileContent {
  fileName: string;
  fileType: string;
  fileContent: string; // base64
}

export interface S3FileContent {
  fileName: string;
  fileType: string;
  s3Key: string;
}

export interface ChatRequest {
  message: string;
  conversationId?: string;
  files?: FileContent[]; // Petit fichiers en base64
  s3Files?: S3FileContent[]; // Gros fichiers uploadés via S3
}

export interface PresignedUrlResponse {
  presignedUrl: string;
  s3Key: string;
  fileName: string;
  fileType: string;
}

export interface ChatResponse {
  response: string;
  conversationId: string;
}

export interface ConversationHistory {
  conversationId: string;
  messages: ChatMessage[];
}

export interface ConversationListItem {
  conversationId: string;
  timestamp: number;
  messageCount: number;
  preview: string;
}

export interface ConversationListResponse {
  conversations: ConversationListItem[];
  count: number;
}

export interface MonthlyUsage {
  month: string;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
}

export interface MonthlyUsageResponse {
  year: number;
  monthlyData: Array<{
    month: string;
    inputTokens: number;
    outputTokens: number;
    costUsd: number;
  }>;
  totalCost: number;
}

class ChatService {
  private streamUrl: string;

  constructor() {
    // URL de la Lambda Function URL pour le streaming avec Lambda Web Adapter
    this.streamUrl = 'https://bvwj44lx75sujykpsjhpf5kdu40ftzvw.lambda-url.eu-west-3.on.aws';
  }

  private async getAuthHeaders(): Promise<Headers> {
    const token = localStorage.getItem('idToken');
    const headers = new Headers({
      'Content-Type': 'application/json',
    });

    if (token) {
      headers.append('Authorization', `Bearer ${token}`);
    }

    return headers;
  }

  async sendMessage(request: ChatRequest): Promise<ChatResponse> {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${this.streamUrl}/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error sending chat message:', error);
      throw new Error('Failed to send message');
    }
  }

  async *sendMessageStream(request: ChatRequest): AsyncGenerator<{ type: 'chunk' | 'start' | 'end'; content?: string; conversationId?: string; timestamp?: number }, void, unknown> {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${this.streamUrl}/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.trim()) continue;

            try {
              const data = JSON.parse(line);
              
              if (data.type === 'chunk' && data.content) {
                yield { type: 'chunk', content: data.content };
              } else if (data.type === 'start') {
                yield { type: 'start', conversationId: data.conversationId, timestamp: data.timestamp };
              } else if (data.type === 'end') {
                yield { type: 'end', timestamp: data.timestamp };
              } else if (data.type === 'error') {
                console.error('Stream error from server:', data.content);
                throw new Error(data.content || 'Streaming error');
              }
            } catch (e) {
              if (e instanceof SyntaxError) {
                console.warn('Failed to parse line:', line);
              } else {
                throw e;
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
    } catch (error) {
      console.error('Error in streaming chat:', error);
      throw new Error('Failed to stream message');
    }
  }

  async listConversations(): Promise<ConversationListResponse> {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${this.streamUrl}/conversations`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error listing conversations:', error);
      throw new Error('Failed to list conversations');
    }
  }

  async getConversationHistory(conversationId: string): Promise<ConversationHistory> {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${this.streamUrl}/conversations/${conversationId}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 404) {
          return { conversationId, messages: [] };
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting conversation history:', error);
      throw new Error('Failed to get conversation history');
    }
  }

  async deleteConversation(conversationId: string): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${this.streamUrl}/conversations/${conversationId}`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
      throw new Error('Failed to delete conversation');
    }
  }

  async deleteAllConversations(): Promise<{ deletedCount: number; message: string }> {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${this.streamUrl}/conversations`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting all conversations:', error);
      throw new Error('Failed to delete all conversations');
    }
  }

  async getCurrentMonthUsage(): Promise<MonthlyUsage> {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${this.streamUrl}/usage/current-month`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting usage stats:', error);
      throw new Error('Failed to get usage stats');
    }
  }

  async getMonthlyUsageYear(): Promise<MonthlyUsageResponse> {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${this.streamUrl}/usage/monthly-year`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting monthly usage:', error);
      throw new Error('Failed to get monthly usage');
    }
  }

  async processFiles(files: File[]): Promise<FileContent[]> {
    const fileContents: FileContent[] = [];

    for (const file of files) {
      try {
        const base64Content = await this.fileToBase64(file);
        fileContents.push({
          fileName: file.name,
          fileType: file.type || this.getFileTypeFromName(file.name),
          fileContent: base64Content
        });
      } catch (error) {
        console.error(`Error processing file ${file.name}:`, error);
        throw new Error(`Failed to process file: ${file.name}`);
      }
    }

    return fileContents;
  }

  // Seuil de taille pour utiliser S3 (3 MB en base64 ≈ 2.25 MB fichier original)
  private readonly S3_THRESHOLD = 3 * 1024 * 1024;

  async processFilesWithS3(files: File[], conversationId: string): Promise<{
    base64Files: FileContent[];
    s3Files: S3FileContent[];
  }> {
    const base64Files: FileContent[] = [];
    const s3Files: S3FileContent[] = [];

    for (const file of files) {
      try {
        // Pour les gros fichiers, utiliser S3
        if (file.size > this.S3_THRESHOLD) {
          console.log(`Uploading large file via S3: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
          const s3File = await this.uploadFileToS3(file, conversationId);
          s3Files.push(s3File);
        } else {
          // Pour les petits fichiers, utiliser base64
          const base64Content = await this.fileToBase64(file);
          base64Files.push({
            fileName: file.name,
            fileType: file.type || this.getFileTypeFromName(file.name),
            fileContent: base64Content
          });
        }
      } catch (error) {
        console.error(`Error processing file ${file.name}:`, error);
        throw new Error(`Failed to process file: ${file.name}`);
      }
    }

    return { base64Files, s3Files };
  }

  async getPresignedUrl(fileName: string, fileType: string, conversationId: string): Promise<PresignedUrlResponse> {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${this.streamUrl}/upload/presigned-url`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          fileName,
          fileType,
          conversationId
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting presigned URL:', error);
      throw new Error('Failed to get presigned URL');
    }
  }

  async uploadFileToS3(file: File, conversationId: string): Promise<S3FileContent> {
    const fileType = file.type || this.getFileTypeFromName(file.name);
    
    // Obtenir la presigned URL
    const { presignedUrl, s3Key } = await this.getPresignedUrl(
      file.name,
      fileType,
      conversationId
    );

    // Uploader directement vers S3
    const uploadResponse = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': fileType
      }
    });

    if (!uploadResponse.ok) {
      throw new Error(`Failed to upload to S3: ${uploadResponse.status}`);
    }

    return {
      fileName: file.name,
      fileType,
      s3Key
    };
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          // Supprimer le préfixe "data:type/subtype;base64," pour ne garder que le base64
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        } else {
          reject(new Error('Failed to read file as base64'));
        }
      };
      reader.onerror = error => reject(error);
    });
  }

  private getFileTypeFromName(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();
    
    switch (extension) {
      case 'pdf':
        return 'application/pdf';
      case 'docx':
        return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'txt':
        return 'text/plain';
      case 'json':
        return 'application/json';
      case 'csv':
        return 'text/csv';
      default:
        return 'application/octet-stream';
    }
  }
}

export const chatService = new ChatService();